import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import type { Domain } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { FEATHRMAIL } from '@feathr/extender/global';
import { useEntriConnect } from '@feathr/extender/hooks';
import { useAccount, useLocalUrl, useStore } from '@feathr/extender/state';
import { flattenErrors, logUserEvents } from '@feathr/hooks';

import {
  buildSubdomain,
  DEFAULT_CONTENT_SUBDOMAIN,
  getSubdomainPrefix,
  rebaseSubdomain,
} from './DomainSetupModal.utils';

export enum EDomainSetupStep {
  Domain = 0,
  ContentSubdomain = 1,
  EmailSubdomain = 2,
  Authenticate = 3,
}

export interface IUseDomainSetupModalOptions {
  domain: Domain;
  onClose: () => void;
}

export interface IUseDomainSetupModal {
  contentDomainError?: string;
  contentSubdomain: string;
  emailSubdomain: string;
  handleAuthenticate: () => Promise<void>;
  handleBack: () => void;
  handleCancel: () => Promise<void>;
  handleClose: () => void;
  handleContentSubdomainChange: (value?: string) => void;
  handleDomainChange: (value?: string) => void;
  handleEmailSubdomainChange: (value?: string) => void;
  handleNext: () => Promise<void>;
  isCheckingDomain: boolean;
  isEmailSubdomainLocked: boolean;
  isSubmitting: boolean;
  nextErrors: string[];
  step: EDomainSetupStep;
}

export function useDomainSetupModal({
  domain,
  onClose,
}: IUseDomainSetupModalOptions): IUseDomainSetupModal {
  const { t } = useTranslation();
  const account = useAccount();
  const { Domains } = useStore();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { canConnect, openEntri } = useEntriConnect();
  const [step, setStep] = useState(EDomainSetupStep.Domain);
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [matchingDomains, setMatchingDomains] = useState<Domain[]>([]);

  const isEmailSubdomainLocked = matchingDomains.length > 0;

  function getPrefix(fullDomain?: string): string {
    return getSubdomainPrefix(fullDomain, domain.get('email_domain') ?? '');
  }

  function getContentDomainError(): string | undefined {
    const contentDomain = domain.get('content_domain');
    return matchingDomains.some((match) => match.get('content_domain') === contentDomain)
      ? t('This content domain is already in use for the given root domain.')
      : undefined;
  }

  function getStepErrors(): string[] {
    switch (step) {
      case EDomainSetupStep.Domain:
        if (!domain.get('email_domain')) {
          return [t('Enter a domain.')];
        }
        return flattenErrors(domain.validate(['email_domain'], false, 'grouped').errors);

      case EDomainSetupStep.ContentSubdomain: {
        if (!getPrefix(domain.get('content_domain'))) {
          return [t('Enter a content serving subdomain.')];
        }
        const errors = flattenErrors(domain.validate(['content_domain'], false, 'grouped').errors);
        const contentDomainError = getContentDomainError();
        return contentDomainError ? [...errors, contentDomainError] : errors;
      }

      case EDomainSetupStep.EmailSubdomain:
        if (!getPrefix(domain.get('mail_from'))) {
          return [t('Enter an email sending subdomain.')];
        }
        return flattenErrors(domain.validate(['mail_from'], false, 'grouped').errors);

      default:
        return [];
    }
  }

  function handleDomainChange(value?: string): void {
    const previousRoot = domain.get('email_domain') ?? '';
    const nextRoot = value ?? '';
    const contentDomain = rebaseSubdomain(
      domain.get('content_domain'),
      previousRoot,
      nextRoot,
      DEFAULT_CONTENT_SUBDOMAIN,
    );
    const mailFrom = isEmailSubdomainLocked
      ? buildSubdomain('', nextRoot, FEATHRMAIL)
      : rebaseSubdomain(domain.get('mail_from'), previousRoot, nextRoot, FEATHRMAIL);
    if (isEmailSubdomainLocked) {
      setMatchingDomains([]);
    }
    domain.set({ content_domain: contentDomain, email_domain: nextRoot, mail_from: mailFrom });
  }

  function handleContentSubdomainChange(value = ''): void {
    domain.set({ content_domain: `${value}.${domain.get('email_domain')}` });
  }

  function handleEmailSubdomainChange(value = ''): void {
    if (isEmailSubdomainLocked) {
      return;
    }
    domain.set({ mail_from: `${value}.${domain.get('email_domain')}` });
  }

  async function advanceFromDomainStep(): Promise<void> {
    const rootDomain = domain.get('email_domain');
    setMatchingDomains([]);
    if (!domain.get('content_domain')) {
      domain.set({ content_domain: buildSubdomain('', rootDomain, DEFAULT_CONTENT_SUBDOMAIN) });
    }
    if (!domain.get('mail_from')) {
      domain.set({ mail_from: buildSubdomain('', rootDomain, FEATHRMAIL) });
    }

    setIsCheckingDomain(true);
    try {
      const response = await domain.getMatchingDomains();
      if (response.isErrored) {
        throw response.error;
      }
      const matches = [...response.models];
      if (matches.length) {
        domain.set({ mail_from: matches[0].get('mail_from') });
      }
      setMatchingDomains(matches);
      setStep(EDomainSetupStep.ContentSubdomain);
    } catch {
      toast(t("Couldn't create domain. Try again, or contact support."), { type: 'error' });
    } finally {
      setIsCheckingDomain(false);
    }
  }

  async function handleNext(): Promise<void> {
    if (isCheckingDomain || getStepErrors().length) {
      return;
    }
    if (step === EDomainSetupStep.Domain) {
      await advanceFromDomainStep();
      return;
    }
    setStep(Math.min(step + 1, EDomainSetupStep.Authenticate));
  }

  function handleBack(): void {
    setStep((current) => Math.max(current - 1, EDomainSetupStep.Domain));
  }

  async function submit(shouldOpenEntri: boolean): Promise<void> {
    setIsSubmitting(true);
    try {
      const saved = await Domains.add(domain);
      if (saved.isErrored) {
        toast(t("Couldn't add domain. {{error}}", { error: saved.error?.message }), {
          type: 'error',
        });
        return;
      }

      logUserEvents({
        'Added custom domain': { account_id: account.id, domain_id: saved.id },
      });
      toast(t('Added domain.'), { type: 'success' });
      if (shouldOpenEntri && canConnect(saved)) {
        await openEntri(saved);
      }
      onClose();
      history.push(localUrl(saved.getItemUrl()));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleAuthenticate(): Promise<void> {
    await submit(true);
  }

  async function handleCancel(): Promise<void> {
    await submit(false);
  }

  function handleClose(): void {
    if (isSubmitting) {
      return;
    }
    onClose();
  }

  return {
    contentDomainError: getContentDomainError(),
    contentSubdomain: getPrefix(domain.get('content_domain')),
    emailSubdomain: getPrefix(domain.get('mail_from')),
    handleAuthenticate,
    handleBack,
    handleCancel,
    handleClose,
    handleContentSubdomainChange,
    handleDomainChange,
    handleEmailSubdomainChange,
    handleNext,
    isCheckingDomain,
    isEmailSubdomainLocked,
    isSubmitting,
    nextErrors: getStepErrors(),
    step,
  };
}
